/**
 * 🔍 ENHANCED VERIFICATION VIEWER - Advanced document review with thumbnails
 * Combines document thumbnail gallery with comprehensive verification review
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Divider,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Badge,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Person,
  Description,
  Security,
  DirectionsCar,
  CheckCircle,
  Cancel,
  Warning,
  Info,
  ExpandMore,
  Close,
  Timeline,
  Flag,
  Message,
  History,
  Star,
  Verified,
} from '@mui/icons-material';

import { DocumentThumbnailGallery, type DocumentInfo } from '@/components/documents/DocumentThumbnailGallery';
import { DocumentQualityAnalyzer } from '@/components/documents/DocumentQualityAnalyzer';
import { AdminVerificationRequest } from '@/services/verificationService';

interface EnhancedVerificationViewerProps {
  open: boolean;
  onClose: () => void;
  verificationRequest: AdminVerificationRequest;
  onApprove?: (verificationId: string, notes?: string) => Promise<void>;
  onReject?: (verificationId: string, reason: string, notes?: string) => Promise<void>;
  onFlag?: (verificationId: string, reason: string) => Promise<void>;
}

const REJECTION_REASONS = [
  'Document quality too poor to read',
  'Document appears to be fake/altered',
  'Photo does not match ID document',
  'Document is expired',
  'Wrong document type submitted',
  'Personal information mismatch',
  'Missing required information',
  'Duplicate submission detected',
  'Other (specify in notes)'
];

const VERIFICATION_STAGES = [
  { id: 'identity', label: 'Identity Verification', icon: <Person /> },
  { id: 'documentation', label: 'Document Review', icon: <Description /> },
  { id: 'verification', label: 'Cross-Verification', icon: <Security /> },
  { id: 'approval', label: 'Final Approval', icon: <CheckCircle /> }
];

export const EnhancedVerificationViewer: React.FC<EnhancedVerificationViewerProps> = ({
  open,
  onClose,
  verificationRequest,
  onApprove,
  onReject,
  onFlag
}) => {
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'flag' | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);

  // Convert verification request to document format
  useEffect(() => {
    if (!verificationRequest) return;

    const docs: DocumentInfo[] = [];

    // Add document image if available
    if (verificationRequest.documentImageUrl) {
      docs.push({
        id: 'document',
        type: 'national_id',
        url: verificationRequest.documentImageUrl,
        fileName: `${verificationRequest.documentType}_document.jpg`,
        uploadedAt: verificationRequest.uploadedAt,
        status: verificationRequest.status === 'verified' ? 'approved' : 
                verificationRequest.status === 'rejected' ? 'rejected' : 'pending',
        quality: 'good' // Could be analyzed from actual document
      });
    }

    // Add selfie image if available
    if (verificationRequest.selfieImageUrl) {
      docs.push({
        id: 'selfie',
        type: 'selfie',
        url: verificationRequest.selfieImageUrl,
        fileName: 'selfie_verification.jpg',
        uploadedAt: verificationRequest.uploadedAt,
        status: verificationRequest.status === 'verified' ? 'approved' : 
                verificationRequest.status === 'rejected' ? 'rejected' : 'pending',
        quality: 'good'
      });
    }

    setDocuments(docs);
  }, [verificationRequest]);

  const handleAction = async (action: 'approve' | 'reject' | 'flag') => {
    if (!verificationRequest) return;

    setIsProcessing(true);
    try {
      switch (action) {
        case 'approve':
          await onApprove?.(verificationRequest.verificationId, adminNotes || 'Verification approved');
          break;
        case 'reject':
          await onReject?.(verificationRequest.verificationId, rejectionReason, adminNotes);
          break;
        case 'flag':
          await onFlag?.(verificationRequest.verificationId, flagReason);
          break;
      }
      onClose();
    } catch (error) {
      console.error(`Error ${action}ing verification:`, error);
    } finally {
      setIsProcessing(false);
      setActionType(null);
    }
  };

  const getVerificationProgress = () => {
    const completedStages = [];
    
    if (verificationRequest.documentImageUrl) completedStages.push('identity');
    if (verificationRequest.selfieImageUrl) completedStages.push('documentation');
    if (verificationRequest.status !== 'pending_review') completedStages.push('verification');
    if (verificationRequest.status === 'verified') completedStages.push('approval');

    return (completedStages.length / VERIFICATION_STAGES.length) * 100;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'success';
      case 'rejected': return 'error';
      case 'pending_review': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle />;
      case 'rejected': return <Cancel />;
      case 'pending_review': return <Warning />;
      default: return <Info />;
    }
  };

  const UserInfoCard = () => (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ width: 56, height: 56 }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="h6">
              {verificationRequest.metadata?.firstName || 'User'} {verificationRequest.metadata?.lastName || ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {verificationRequest.verificationId}
            </Typography>
            <Chip
              icon={getStatusIcon(verificationRequest.status)}
              label={verificationRequest.status.replace('_', ' ').toUpperCase()}
              color={getStatusColor(verificationRequest.status)}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Document Type</Typography>
            <Chip 
              label={verificationRequest.documentType.replace('_', ' ').toUpperCase()} 
              variant="outlined" 
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Submitted</Typography>
            <Typography variant="body2">
              {new Date(verificationRequest.uploadedAt).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Region</Typography>
            <Typography variant="body2">
              {verificationRequest.metadata?.region || 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Priority</Typography>
            <Chip 
              label={verificationRequest.priority || 'Medium'} 
              color={verificationRequest.priority === 'high' ? 'error' : 'default'}
              size="small"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const VerificationProgress = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
          <Timeline />
          Verification Progress
        </Typography>
        
        <Box mb={2}>
          <LinearProgress 
            variant="determinate" 
            value={getVerificationProgress()} 
            sx={{ height: 8, borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {Math.round(getVerificationProgress())}% Complete
          </Typography>
        </Box>

        <Stack spacing={1}>
          {VERIFICATION_STAGES.map((stage, index) => {
            const isCompleted = getVerificationProgress() > (index / VERIFICATION_STAGES.length) * 100;
            return (
              <Box key={stage.id} display="flex" alignItems="center" gap={1}>
                <Box 
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%',
                    bgcolor: isCompleted ? 'success.main' : 'grey.300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                >
                  {React.cloneElement(stage.icon, { fontSize: 'small' })}
                </Box>
                <Typography 
                  variant="body2" 
                  color={isCompleted ? 'text.primary' : 'text.secondary'}
                >
                  {stage.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { width: '95vw', height: '95vh', maxHeight: 'none' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <Verified />
            Verification Review
            <Badge badgeContent={documents.length} color="primary" />
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Left Column - User Info & Progress */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <UserInfoCard />
              <VerificationProgress />
              
              {/* Document Quality Analysis */}
              {documents.map(doc => (
                <DocumentQualityAnalyzer
                  key={doc.id}
                  imageUrl={doc.url}
                  documentType={doc.type}
                  showDetails={false}
                  compact={true}
                />
              ))}
            </Stack>
          </Grid>

          {/* Right Column - Documents */}
          <Grid item xs={12} lg={8}>
            <DocumentThumbnailGallery
              documents={documents}
              title="Verification Documents"
              showQualityIndicators={true}
              showBatchActions={true}
              allowFullscreen={true}
              onDocumentApprove={(docId, notes) => {
                // Handle individual document approval
              }}
              onDocumentReject={(docId, reason) => {
                // Handle individual document rejection
              }}
              onDocumentFlag={(docId, reason) => {
                // Handle document flagging
              }}
              onBatchAction={(action, docIds) => {
                // Handle batch document actions
              }}
            />
          </Grid>
        </Grid>

        {/* Action Confirmation Sections */}
        {actionType && (
          <Box mt={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {actionType === 'approve' ? 'Approve Verification' : 
                   actionType === 'reject' ? 'Reject Verification' : 'Flag for Review'}
                </Typography>

                {actionType === 'reject' && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Rejection Reason</InputLabel>
                    <Select
                      value={rejectionReason}
                      label="Rejection Reason"
                      onChange={(e) => setRejectionReason(e.target.value)}
                    >
                      {REJECTION_REASONS.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {actionType === 'flag' && (
                  <TextField
                    fullWidth
                    label="Flag Reason"
                    value={flagReason}
                    onChange={(e) => setFlagReason(e.target.value)}
                    placeholder="Describe why this verification needs attention..."
                    sx={{ mb: 2 }}
                  />
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Admin Notes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add any additional notes about this verification..."
                />
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between' }}>
          <Box>
            <Button onClick={onClose} disabled={isProcessing}>
              Close
            </Button>
          </Box>

          <Box>
            {actionType ? (
              <Stack direction="row" spacing={1}>
                <Button 
                  onClick={() => setActionType(null)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleAction(actionType)}
                  variant="contained"
                  color={actionType === 'approve' ? 'success' : 'error'}
                  disabled={isProcessing || (actionType === 'reject' && !rejectionReason) || (actionType === 'flag' && !flagReason)}
                >
                  {isProcessing ? 'Processing...' : 
                   actionType === 'approve' ? 'Confirm Approval' :
                   actionType === 'reject' ? 'Confirm Rejection' : 'Confirm Flag'}
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<Flag />}
                  onClick={() => setActionType('flag')}
                  color="warning"
                  variant="outlined"
                >
                  Flag
                </Button>
                <Button
                  startIcon={<Cancel />}
                  onClick={() => setActionType('reject')}
                  color="error"
                  variant="outlined"
                >
                  Reject
                </Button>
                <Button
                  startIcon={<CheckCircle />}
                  onClick={() => setActionType('approve')}
                  color="success"
                  variant="contained"
                >
                  Approve
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};